import { useEffect, useState, type FormEvent } from 'react';
import { Save, Building2 } from 'lucide-react';
import { toast } from 'sonner';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Select } from '@/components/ui/Select';
import { LoaderScreen } from '@/components/ui/LoaderScreen';
import { useCompanyProfile, useSaveCompanyProfile, type CompanyProfile } from '@/hooks/useSettings';

const DEFAULT: CompanyProfile = {
  legalName: 'Talbert Architectural Panels & Doors, Inc.',
  dba: 'Talbert Architectural',
  ein: '',
  fscCertificate: 'C014235',
  address1: '711 S Stimson Ave',
  address2: '',
  city: 'City of Industry',
  state: 'CA',
  postalCode: '91745',
  country: 'United States',
  phone: '714-671-9700',
  email: 'office@talbert.app',
  website: 'https://talbertusa.com',
  officeHours: '7:30am – 4:00pm',
  shippingHours: '6:00am – 2:30pm',
  fiscalYearStart: '01-01',
  defaultCurrency: 'USD',
  taxId: '',
};

export function CompanySettings() {
  const { data, isLoading } = useCompanyProfile();
  const save = useSaveCompanyProfile();
  const [form, setForm] = useState<CompanyProfile>(DEFAULT);
  const [dirty, setDirty] = useState(false);

  useEffect(() => {
    if (data) { setForm({ ...DEFAULT, ...data }); setDirty(false); }
  }, [data]);

  function update<K extends keyof CompanyProfile>(key: K, val: CompanyProfile[K]) {
    setForm((f) => ({ ...f, [key]: val }));
    setDirty(true);
  }

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    try {
      await save.mutateAsync(form);
      toast.success('Company profile saved');
      setDirty(false);
    } catch (err) {
      toast.error((err as { message?: string }).message ?? 'Failed to save profile.');
    }
  }

  if (isLoading) return <LoaderScreen />;

  return (
    <form onSubmit={onSubmit} className="space-y-6">
      <div className="flex items-end justify-between gap-4">
        <div>
          <div className="kicker mb-2">Company · Profile</div>
          <h2 className="font-display text-2xl text-text">Business Information</h2>
        </div>
        <Button type="submit" leftIcon={<Save size={14} />} loading={save.isPending} disabled={!dirty}>
          Save Changes
        </Button>
      </div>

      {/* Identity */}
      <Section
        kicker="Identity"
        title="Legal & trade names"
        description="Used on invoices, quotes, and outbound correspondence."
      >
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Input
            label="Legal Name"
            value={form.legalName}
            onChange={(e) => update('legalName', e.target.value)}
            required
            className="md:col-span-2"
          />
          <Input label="DBA / Trade Name" value={form.dba ?? ''} onChange={(e) => update('dba', e.target.value)} />
          <Input label="EIN" value={form.ein ?? ''} onChange={(e) => update('ein', e.target.value)} placeholder="XX-XXXXXXX" />
          <Input
            label="FSC® Certificate"
            value={form.fscCertificate ?? ''}
            onChange={(e) => update('fscCertificate', e.target.value)}
            hint="First veneer company certified in California."
          />
          <Input label="State Tax ID" value={form.taxId ?? ''} onChange={(e) => update('taxId', e.target.value)} />
        </div>
      </Section>

      {/* Address */}
      <Section kicker="Address" title="Primary location" description="City of Industry headquarters and shipping origin.">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Input
            label="Street 1"
            value={form.address1}
            onChange={(e) => update('address1', e.target.value)}
            required
            className="md:col-span-2"
          />
          <Input
            label="Street 2"
            value={form.address2 ?? ''}
            onChange={(e) => update('address2', e.target.value)}
            className="md:col-span-2"
          />
          <Input label="City"        value={form.city}       onChange={(e) => update('city', e.target.value)} required />
          <Input label="State"       value={form.state}      onChange={(e) => update('state', e.target.value)} required />
          <Input label="Postal Code" value={form.postalCode} onChange={(e) => update('postalCode', e.target.value)} required />
          <Input label="Country"     value={form.country}    onChange={(e) => update('country', e.target.value)} required />
        </div>
      </Section>

      {/* Contact */}
      <Section kicker="Contact" title="Public-facing channels">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Input label="Phone"          value={form.phone}          onChange={(e) => update('phone', e.target.value)} required />
          <Input label="Email"          type="email" value={form.email} onChange={(e) => update('email', e.target.value)} required />
          <Input label="Website"        value={form.website ?? ''}  onChange={(e) => update('website', e.target.value)} className="md:col-span-2" />
          <Input label="Office Hours"   value={form.officeHours ?? ''} onChange={(e) => update('officeHours', e.target.value)} />
          <Input label="Shipping Hours" value={form.shippingHours ?? ''} onChange={(e) => update('shippingHours', e.target.value)} />
        </div>
      </Section>

      {/* Finance */}
      <Section kicker="Finance" title="Reporting defaults" description="Used by reports, invoices, and accounting close.">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Input
            label="Fiscal Year Start"
            value={form.fiscalYearStart}
            onChange={(e) => update('fiscalYearStart', e.target.value)}
            placeholder="MM-DD"
            hint="e.g. 01-01 for calendar year."
          />
          <Select
            label="Default Currency"
            value={form.defaultCurrency}
            onChange={(e) => update('defaultCurrency', e.target.value)}
            options={[
              { value: 'USD', label: 'USD — US Dollar' },
              { value: 'CAD', label: 'CAD — Canadian Dollar' },
              { value: 'MXN', label: 'MXN — Mexican Peso' },
            ]}
          />
        </div>
      </Section>

      {/* Footer save bar */}
      {dirty && (
        <div className="sticky bottom-4 z-10 flex items-center justify-between p-4 bg-bg-raised border border-brass-400/40 rounded-sm shadow-raised animate-slide-up">
          <div className="flex items-center gap-3 text-sm">
            <Building2 size={16} className="text-brass-300" />
            <span className="text-text">Unsaved changes to company profile.</span>
          </div>
          <Button type="submit" leftIcon={<Save size={14} />} loading={save.isPending}>
            Save Changes
          </Button>
        </div>
      )}
    </form>
  );
}

function Section({
  kicker, title, description, children,
}: {
  kicker: string; title: string; description?: string; children: React.ReactNode;
}) {
  return (
    <Card>
      <header className="mb-6 pb-4 border-b border-border">
        <div className="kicker mb-1.5">{kicker}</div>
        <h3 className="font-display text-lg text-text">{title}</h3>
        {description && <p className="text-xs text-text-muted mt-1.5">{description}</p>}
      </header>
      {children}
    </Card>
  );
}
